import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { SakuraField } from "../components/SakuraField";
import { SmartImg } from "../components/SmartImg";
import { ReportModal } from "../components/ReportModal";

type KanaItem = { id: string; char: string; romaji: string; row: string; order: number };
type AttemptResult = { is_correct: boolean; correct_romaji: string; xp_awarded: number; coin_awarded: number };

const MAX_HEARTS = 5;
const LESSON_SIZE = 20;

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export function KanaPracticePage() {
  const { t } = useTranslation(["common", "learner"]);
  const { kind } = useParams();
  const queryClient = useQueryClient();

  const { data: allKana } = useQuery({
    queryKey: ["kana", kind],
    queryFn: () => api<KanaItem[]>(`/api/learner/kana?kind=${kind}`),
    enabled: kind === "hiragana" || kind === "katakana",
  });

  const [lessonItems, setLessonItems] = useState<KanaItem[] | null>(null);
  const [index, setIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hearts, setHearts] = useState(MAX_HEARTS);
  const [refillOpen, setRefillOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [totalXp, setTotalXp] = useState(0);
  const [totalCoins, setTotalCoins] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [streakInLesson, setStreakInLesson] = useState(0);

  // データロード or kind 変更時に20問のレッスンセットを構築
  useEffect(() => {
    if (!allKana) return;
    const picked = shuffle(allKana).slice(0, Math.min(LESSON_SIZE, allKana.length));
    setLessonItems(picked);
    setIndex(0);
    setSelectedId(null);
    setFeedback(null);
    setHearts(MAX_HEARTS);
    setRefillOpen(false);
    setCompleted(false);
    setTotalXp(0);
    setTotalCoins(0);
    setCorrectCount(0);
    setStreakInLesson(0);
  }, [kind, allKana]);

  const currentKana = lessonItems?.[index];

  const choices = useMemo(() => {
    if (!allKana || !currentKana) return [];
    const others = allKana.filter((k) => k.id !== currentKana.id);
    const sample: KanaItem[] = [];
    while (sample.length < 3 && others.length > 0) {
      const pick = others.splice(Math.floor(Math.random() * others.length), 1)[0];
      sample.push(pick);
    }
    const all = [currentKana, ...sample];
    return all.sort(() => Math.random() - 0.5);
  // 問題が変わるたびに再シャッフル
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKana?.id, allKana]);

  function startNewLesson() {
    if (!allKana) return;
    const picked = shuffle(allKana).slice(0, Math.min(LESSON_SIZE, allKana.length));
    setLessonItems(picked);
    setIndex(0);
    setSelectedId(null);
    setFeedback(null);
    setHearts(MAX_HEARTS);
    setRefillOpen(false);
    setCompleted(false);
    setTotalXp(0);
    setTotalCoins(0);
    setCorrectCount(0);
    setStreakInLesson(0);
  }

  function pick(id: string) {
    if (feedback || submitting) return;
    setSelectedId(id);
  }

  async function check() {
    if (!currentKana || submitting || !selectedId) return;
    const chosen = choices.find((c) => c.id === selectedId);
    if (!chosen) return;
    setSubmitting(true);
    try {
      const result = await api<AttemptResult>("/api/learner/kana/attempts", {
        method: "POST",
        body: { kana_id: currentKana.id, selected_romaji: chosen.romaji },
      });
      setFeedback(result);
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      if (result.is_correct) {
        setTotalXp((x) => x + (result.xp_awarded ?? 0));
        setTotalCoins((c) => c + (result.coin_awarded ?? result.xp_awarded ?? 0));
        setCorrectCount((n) => n + 1);
        setStreakInLesson((s) => s + 1);
      } else {
        setStreakInLesson(0);
        setHearts((h) => {
          const next = Math.max(0, h - 1);
          if (next === 0) setRefillOpen(true);
          return next;
        });
      }
    } catch (err) {
      console.error("Submit failed:", err);
      const isCorrect = chosen.romaji === currentKana.romaji;
      setFeedback({
        is_correct: isCorrect,
        correct_romaji: currentKana.romaji,
        xp_awarded: 0,
        coin_awarded: 0,
      });
      if (isCorrect) {
        setCorrectCount((n) => n + 1);
        setStreakInLesson((s) => s + 1);
      } else {
        setStreakInLesson(0);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function skip() {
    if (feedback || submitting || !currentKana) return;
    // SKIP は不正解扱い（ハート減らさず・XP なしで次へ）
    setFeedback({
      is_correct: false,
      correct_romaji: currentKana.romaji,
      xp_awarded: 0,
      coin_awarded: 0,
    });
  }

  function next() {
    if (!lessonItems) return;
    if (index + 1 >= lessonItems.length) {
      setCompleted(true);
      return;
    }
    setFeedback(null);
    setSelectedId(null);
    setIndex((i) => i + 1);
  }

  function refill() {
    setHearts(MAX_HEARTS);
    setRefillOpen(false);
  }

  // PC キーボード操作（Duolingo準拠）：1〜4で選択、Enterで確定/次、SpaceでSKIP
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (refillOpen || reportOpen || completed) return;
      if (!lessonItems || !currentKana) return;
      // IME 入力中や input フォーカス時は無視
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e as KeyboardEvent).isComposing) return;

      const num = Number(e.key);
      if (Number.isInteger(num) && num >= 1 && num <= choices.length) {
        e.preventDefault();
        if (!feedback) setSelectedId(choices[num - 1].id);
        return;
      }
      // 矢印キーで2x2 grid 内をフォーカス移動
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown"
      ) {
        if (feedback) return;
        e.preventDefault();
        const curIdx = selectedId ? choices.findIndex((c) => c.id === selectedId) : -1;
        const cols = 2;
        let nextIdx = curIdx;
        if (curIdx === -1) {
          nextIdx = 0;
        } else if (e.key === "ArrowLeft") {
          nextIdx = Math.max(0, curIdx - 1);
        } else if (e.key === "ArrowRight") {
          nextIdx = Math.min(choices.length - 1, curIdx + 1);
        } else if (e.key === "ArrowUp") {
          nextIdx = Math.max(0, curIdx - cols);
        } else if (e.key === "ArrowDown") {
          nextIdx = Math.min(choices.length - 1, curIdx + cols);
        }
        if (nextIdx >= 0 && nextIdx < choices.length) {
          setSelectedId(choices[nextIdx].id);
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (feedback) next();
        else if (selectedId) void check();
        return;
      }
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (!feedback) skip();
        return;
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choices, feedback, selectedId, refillOpen, reportOpen, completed, lessonItems, currentKana?.id]);

  if (completed && lessonItems) {
    return (
      <LessonComplete
        xp={totalXp}
        coins={totalCoins}
        correct={correctCount}
        total={lessonItems.length}
        onRetry={startNewLesson}
      />
    );
  }

  if (!lessonItems || !currentKana) {
    return <div className="p-8 text-center text-muted text-xl">{t("common:loading")}</div>;
  }

  const progressPct = Math.round(((index + 1) / lessonItems.length) * 100);
  const canCheck = !!selectedId && !feedback && !submitting;

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-sakura/10 via-white to-blue-50">
      <SakuraField />

      {/* Top bar (max-w-3xl mx-auto で下部バーと幅を揃える) -------------- */}
      <header className="absolute top-0 left-0 right-0 z-30 p-3 sm:p-6">
        <div className="max-w-3xl mx-auto relative">
          <AnimatePresence>
            {streakInLesson >= 2 && (
              <motion.div
                key={`streak-${streakInLesson}`}
                initial={{ opacity: 0, y: 6, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
                className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-bold tracking-wide text-yellow-600 whitespace-nowrap pointer-events-none"
              >
                {t("learner:streak_in_row", { n: streakInLesson })}
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/room"
              aria-label={t("learner:my_room")}
              className="bg-white/90 backdrop-blur rounded-full shadow-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-primary text-lg sm:text-xl font-bold hover:bg-white shrink-0"
            >
              ×
            </Link>
            <div className="flex-1 h-3 sm:h-4 bg-white/60 rounded-full overflow-hidden shadow-inner">
              <motion.div
                className={`h-full ${
                  streakInLesson >= 2
                    ? "bg-gradient-to-r from-yellow-300 to-yellow-500"
                    : "bg-gradient-to-r from-sakura to-primary"
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
            <div className="bg-white/90 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg px-2.5 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 shrink-0">
              <HeartIcon filled={hearts > 0} />
              <span className={`text-base sm:text-lg font-bold ${hearts > 0 ? "text-accent" : "text-muted"}`}>{hearts}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Practice content ---------------------------------------------- */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full px-4 sm:px-6 pt-20 sm:pt-24 pb-28 sm:pb-32">
        <p className="text-sm sm:text-lg lg:text-xl text-muted mb-3 sm:mb-6 text-center">{t("learner:select_correct_romaji")}</p>

        <motion.div
          key={currentKana.id}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-3xl sm:rounded-[2rem] shadow-2xl px-10 py-8 sm:px-16 sm:py-12 lg:px-20 lg:py-16 mb-6 sm:mb-10"
        >
          <div className="text-[6.5rem] sm:text-[9rem] lg:text-[11rem] leading-none font-bold text-primary">{currentKana.char}</div>
        </motion.div>

        <div className="grid grid-cols-2 gap-3 sm:gap-5 w-full max-w-2xl">
          {choices.map((c, i) => {
            const isSelected = selectedId === c.id;
            return (
              <motion.button
                key={c.id}
                whileHover={!feedback ? { scale: 1.03 } : {}}
                whileTap={!feedback ? { scale: 0.97 } : {}}
                disabled={!!feedback || submitting}
                onClick={() => pick(c.id)}
                onMouseDown={(e) => e.preventDefault()}
                tabIndex={-1}
                className={`relative bg-white border-2 rounded-2xl py-4 sm:py-5 text-xl sm:text-2xl font-bold transition shadow-sm hover:shadow-xl disabled:opacity-50 focus:outline-none ${
                  isSelected
                    ? "border-primary bg-primary/10 shadow-lg"
                    : "border-gray-200 hover:border-primary/60"
                }`}
              >
                {c.romaji}
                <span
                  aria-hidden
                  className="hidden lg:flex absolute bottom-2 right-2 w-6 h-6 rounded border border-gray-300 items-center justify-center text-xs text-muted font-bold bg-white"
                >
                  {i + 1}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Bottom bar: SKIP / CHECK / 次へ（固定高さ） -------------------- */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-30 border-t transition-colors ${
          feedback
            ? feedback.is_correct
              ? "border-green-200 bg-green-50/95"
              : "border-orange-200 bg-orange-50/95"
            : "border-gray-200 bg-white/95"
        } backdrop-blur`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 min-h-[76px] sm:min-h-[88px]">
          {feedback ? (
            <FeedbackBar
              feedback={feedback}
              kanaChar={currentKana.char}
              onNext={next}
              onReport={() => setReportOpen(true)}
            />
          ) : (
            <>
              <button
                onClick={skip}
                disabled={submitting}
                className="border-2 border-gray-300 text-muted font-bold rounded-xl sm:rounded-2xl px-5 sm:px-8 py-2.5 sm:py-3 hover:bg-gray-50 disabled:opacity-50 text-sm sm:text-base"
              >
                SKIP
              </button>
              <button
                onClick={check}
                disabled={!canCheck}
                className={`font-bold rounded-xl sm:rounded-2xl px-7 sm:px-10 py-2.5 sm:py-3 shadow transition text-sm sm:text-base ${
                  canCheck
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                CHECK
              </button>
            </>
          )}
        </div>
      </div>

      {/* Report modal -------------------------------------------------- */}
      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        areaId="self_room"
        taskId={currentKana.id}
        context={{
          selected_romaji: choices.find((c) => c.id === selectedId)?.romaji,
          correct_romaji: currentKana.romaji,
          is_correct: feedback?.is_correct ?? null,
        }}
      />

      {/* Hearts refill modal ------------------------------------------- */}
      <AnimatePresence>
        {refillOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl px-10 py-10 max-w-sm w-[90%] text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="mx-auto mb-4 w-fit"
              >
                <HeartIcon filled large />
              </motion.div>
              <h2 className="text-xl font-bold text-primary mb-2">ハートが0になりました</h2>
              <p className="text-sm text-muted mb-6">
                大丈夫、無料で補充できます。<br />気にせず続けましょう。
              </p>
              <button
                onClick={refill}
                className="w-full bg-accent text-white text-lg font-bold py-3 rounded-2xl shadow hover:bg-accent/90 transition"
              >
                無料で補充する
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeedbackBar({
  feedback,
  kanaChar,
  onNext,
  onReport,
}: {
  feedback: AttemptResult;
  kanaChar: string;
  onNext: () => void;
  onReport: () => void;
}) {
  const { t } = useTranslation(["common", "learner"]);
  const ok = feedback.is_correct;
  return (
    <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
      <div className="flex-1 min-w-0">
        <div className={`text-base sm:text-2xl font-bold ${ok ? "text-green-700" : "text-orange-700"} flex items-center gap-2 sm:gap-3 flex-wrap`}>
          <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full ${ok ? "bg-green-600 text-white" : "bg-orange-500 text-white"} text-xs sm:text-base shrink-0`}>
            {ok ? "✓" : "✗"}
          </span>
          <span>{ok ? t("learner:correct") : t("learner:incorrect")}</span>
          {ok && feedback.xp_awarded > 0 && (
            <span className="text-xs sm:text-sm text-primary font-bold">+{feedback.xp_awarded} XP · +{feedback.coin_awarded ?? feedback.xp_awarded} 桜コイン</span>
          )}
          {!ok && (
            <span className="text-sm sm:text-base text-body font-normal">
              {kanaChar} = <strong className="text-primary">{feedback.correct_romaji}</strong>
            </span>
          )}
        </div>
        <button
          onClick={onReport}
          className={`mt-1 inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold tracking-wide uppercase hover:underline ${ok ? "text-green-700" : "text-orange-700"}`}
        >
          <FlagIcon />
          <span>{t("learner:report")}</span>
        </button>
      </div>
      <button
        onClick={onNext}
        className={`font-bold rounded-xl sm:rounded-2xl px-6 sm:px-10 py-2.5 sm:py-3 shadow text-white text-sm sm:text-base shrink-0 ${ok ? "bg-green-600 hover:bg-green-700" : "bg-orange-500 hover:bg-orange-600"}`}
      >
        {t("learner:next")}
      </button>
    </div>
  );
}

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 21V4M4 4l13 4-3 4 3 4-13 0" />
    </svg>
  );
}

function HeartIcon({ filled, large }: { filled: boolean; large?: boolean }) {
  const size = large ? "w-16 h-16" : "w-6 h-6";
  return (
    <svg viewBox="0 0 24 24" className={size} fill={filled ? "#C04A4A" : "none"} stroke="#C04A4A" strokeWidth="2">
      <path d="M12 21s-7-4.5-9.5-9C0.5 8 3 4 6.5 4c2 0 3.5 1.5 5.5 3.5C14 5.5 15.5 4 17.5 4 21 4 23.5 8 21.5 12c-2.5 4.5-9.5 9-9.5 9z" strokeLinejoin="round" />
    </svg>
  );
}

function LessonComplete({
  xp,
  coins,
  correct,
  total,
  onRetry,
}: {
  xp: number;
  coins: number;
  correct: number;
  total: number;
  onRetry: () => void;
}) {
  const { t } = useTranslation(["common", "learner"]);
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-sakura/20 via-white to-blue-50 flex flex-col items-center justify-center px-4 py-8 overflow-hidden">
      <SakuraField />

      {/* アバター（喜び・はずみ） */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-32 h-44 sm:w-40 sm:h-56 lg:w-48 lg:h-64 mb-4 sm:mb-6"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-full h-full"
        >
          <SmartImg
            src="/assets/avatars/avatar_01_student_f.png"
            alt="Avatar"
            className="w-full h-full object-contain drop-shadow-2xl"
            fallback={
              <div className="w-full h-full flex items-center justify-center text-7xl">🎉</div>
            }
          />
        </motion.div>
      </motion.div>

      {/* タイトル */}
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="relative z-10 text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2 text-center"
      >
        {t("learner:lesson_clear")}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.4 }}
        className="relative z-10 text-sm sm:text-base text-muted mb-6 sm:mb-8 text-center"
      >
        {t("learner:lesson_clear_sub")}
      </motion.p>

      {/* 統計 3つ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="relative z-10 grid grid-cols-3 gap-2 sm:gap-4 w-full max-w-md mb-6 sm:mb-8"
      >
        <CompleteStat icon="⚡" label={t("learner:earned_xp_label")} value={`+${xp}`} accent="text-yellow-600" />
        <CompleteStat icon="🌸" label={t("learner:coin_label")} value={`+${coins}`} accent="text-pink-600" />
        <CompleteStat icon="✓" label={t("learner:accuracy")} value={`${accuracy}%`} accent="text-green-600" sub={`${correct}/${total}`} />
      </motion.div>

      {/* アクション */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="relative z-10 w-full max-w-md flex flex-col gap-2 sm:gap-3"
      >
        <button
          onClick={onRetry}
          className="w-full bg-primary text-white font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition text-base sm:text-lg"
        >
          {t("learner:retry_lesson")}
        </button>
        <Link
          to="/room"
          className="w-full bg-white border-2 border-gray-200 text-primary font-bold py-3 rounded-2xl shadow-sm hover:border-primary/60 transition text-base sm:text-lg text-center"
        >
          {t("learner:back_to_home")}
        </Link>
      </motion.div>
    </div>
  );
}

function CompleteStat({
  icon,
  label,
  value,
  accent,
  sub,
}: {
  icon: string;
  label: string;
  value: string;
  accent: string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-md px-2 sm:px-3 py-3 sm:py-4 flex flex-col items-center text-center">
      <div className="text-xl sm:text-2xl mb-1">{icon}</div>
      <div className={`text-lg sm:text-2xl font-bold ${accent} leading-tight`}>{value}</div>
      {sub && <div className="text-[10px] sm:text-xs text-muted leading-tight">{sub}</div>}
      <div className="text-[10px] sm:text-xs text-muted mt-1">{label}</div>
    </div>
  );
}
